import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import { Globe, Monitor, Smartphone, Box, Users, FileText, Cpu, Loader2, ArrowRight, ArrowLeft, CheckCircle, Cloud, BrainCircuit, ShieldCheck, Network, Building2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "Web Development", icon: Globe, label_de: "Web-Anwendung", label_en: "Web Application", label_fr: "Application Web", label_es: "Aplicacion Web", desc_de: "Website, Dashboard, E-Commerce, SaaS", desc_en: "Website, Dashboard, E-Commerce, SaaS", desc_fr: "Site web, Tableau de bord, E-Commerce", desc_es: "Sitio web, Panel, E-Commerce, SaaS" },
  { value: "Mobile App", icon: Smartphone, label_de: "Mobile App", label_en: "Mobile App", label_fr: "Application Mobile", label_es: "App Movil", desc_de: "iOS, Android, React Native, Flutter", desc_en: "iOS, Android, React Native, Flutter", desc_fr: "iOS, Android, React Native, Flutter", desc_es: "iOS, Android, React Native, Flutter" },
  { value: "3D/CAD Design", icon: Box, label_de: "3D / CAD Projekt", label_en: "3D / CAD Project", label_fr: "Projet 3D / CAO", label_es: "Proyecto 3D / CAD", desc_de: "3D-Modellierung, Visualisierung, AR/VR", desc_en: "3D Modeling, Visualization, AR/VR", desc_fr: "Modelisation 3D, Visualisation, AR/VR", desc_es: "Modelado 3D, Visualizacion, AR/VR" },
  { value: "Cloud Migration", icon: Cloud, label_de: "Cloud-Migration & Modernisierung", label_en: "Cloud Migration & Modernization", label_fr: "Migration Cloud & Modernisation", label_es: "Migracion a la Nube y Modernizacion", desc_de: "Legacy → Cloud-native, Disaster Recovery, DB-Konsolidierung", desc_en: "Legacy → cloud-native, disaster recovery, DB consolidation", desc_fr: "Legacy → cloud-native, reprise apres sinistre, consolidation DB", desc_es: "Legacy → cloud-native, recuperacion ante desastres, consolidacion BBDD" },
  { value: "Data & AI Hub", icon: BrainCircuit, label_de: "Data & AI Hub", label_en: "Data & AI Hub", label_fr: "Data & AI Hub", label_es: "Data & AI Hub", desc_de: "Data Warehouse, Custom AI Agents, IoT-Plattform", desc_en: "Data warehouse, custom AI agents, IoT platform", desc_fr: "Data warehouse, agents IA custom, plateforme IoT", desc_es: "Data warehouse, agentes IA, plataforma IoT" },
  { value: "Security & Compliance", icon: ShieldCheck, label_de: "Security & Compliance", label_en: "Security & Compliance", label_fr: "Securite & Conformite", label_es: "Seguridad y Cumplimiento", desc_de: "ISO / nDSG / GDPR-Audit, Cybersecurity-Hardening, IAM", desc_en: "ISO / GDPR / SOC2 audit, cybersecurity hardening, IAM", desc_fr: "Audit ISO / RGPD, durcissement cybersecurite, IAM", desc_es: "Auditoria ISO / RGPD, hardening de ciberseguridad, IAM" },
  { value: "Enterprise Integration", icon: Network, label_de: "Enterprise Integration", label_en: "Enterprise Integration", label_fr: "Integration Entreprise", label_es: "Integracion Empresarial", desc_de: "ERP / CRM-Konnektoren (SAP, Salesforce), Workflow-Automatisierung (IPaaS)", desc_en: "ERP / CRM connectors (SAP, Salesforce), workflow automation (iPaaS)", desc_fr: "Connecteurs ERP / CRM (SAP, Salesforce), automatisation des workflows (iPaaS)", desc_es: "Conectores ERP / CRM (SAP, Salesforce), automatizacion de flujos (iPaaS)" },
];

const STEPS = [
  { icon: Globe, num: 1 },
  { icon: Users, num: 2 },
  { icon: FileText, num: 3 },
];

const ANALYSIS_STEPS_DE = ["Projektbeschreibung wird geparst...", "Requirements werden extrahiert...", "Kostenaufschluesselung wird berechnet...", "Risikoanalyse laeuft...", "Meilensteine werden geplant...", "3D-Modell wird generiert...", "Analyse abgeschlossen!"];
const ANALYSIS_STEPS_EN = ["Parsing project description...", "Extracting requirements...", "Calculating cost breakdown...", "Running risk analysis...", "Planning milestones...", "Generating 3D model...", "Analysis complete!"];

export default function OnboardingWizard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ projectTitle: "", projectCategory: "", projectDescription: "", audience: "", industry: "auto" });
  const [analyzing, setAnalyzing] = useState(false);
  const [currentAnalysisStep, setCurrentAnalysisStep] = useState(0);
  const [error, setError] = useState("");

  // Guests cannot create projects — redirect to login keeping the intended return path
  if (!user) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center px-4">
        <div className="max-w-md text-center glass-surface rounded-2xl p-10 border border-[#00F0FF]/20">
          <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-[#00F0FF]/10">
            <FileText className="w-6 h-6 text-[#00F0FF]" />
          </div>
          <h2 className="font-['Outfit'] text-xl font-semibold text-white mb-2">Konto erforderlich</h2>
          <p className="text-sm text-[#A0A0B0] mb-6">
            Um ein neues Projekt einzustellen, brauchst du ein kostenloses Konto. Die Registrierung dauert weniger als 30 Sekunden.
          </p>
          <button
            onClick={() => navigate("/login")}
            data-testid="onboarding-login-cta"
            className="btn-primary px-6 py-2.5 text-sm font-['Outfit'] font-semibold"
          >
            Jetzt registrieren <ArrowRight className="w-4 h-4 inline ml-1" />
          </button>
        </div>
      </div>
    );
  }

  const analysisSteps = lang === "de" ? ANALYSIS_STEPS_DE : ANALYSIS_STEPS_EN;
  const role = user?.role || "client";

  const handleSubmit = async () => {
    if (!form.projectTitle.trim() || !form.projectDescription.trim() || !form.projectCategory) {
      setError(lang === "de" ? "Bitte fuelle alle Felder aus." : "Please fill in all fields.");
      return;
    }
    setAnalyzing(true);
    setCurrentAnalysisStep(0);
    const interval = setInterval(() => {
      setCurrentAnalysisStep(prev => prev < analysisSteps.length - 2 ? prev + 1 : prev);
    }, 900);
    try {
      const desc = form.audience ? `${form.projectDescription}\n\nZielgruppe: ${form.audience}` : form.projectDescription;
      const payload = { projectTitle: form.projectTitle, projectCategory: form.projectCategory, projectDescription: desc, lang };
      if (form.industry && form.industry !== "auto") payload.industry = form.industry;
      const res = await axios.post(`${API}/projects`, payload, { withCredentials: true });
      clearInterval(interval);
      setCurrentAnalysisStep(analysisSteps.length - 1);
      await new Promise(r => setTimeout(r, 600));
      navigate(`/project/${res.data.id}`);
    } catch (err) {
      clearInterval(interval);
      setError(err.response?.data?.detail || "Fehler bei der Analyse");
      setAnalyzing(false);
    }
  };

  if (role !== "client") {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="glass-surface rounded-lg p-12 text-center max-w-md">
          <p className="text-[#A0A0B0]">{lang === "de" ? "Nur Auftraggeber koennen Projekte erstellen." : "Only clients can create projects."}</p>
        </div>
      </div>
    );
  }

  if (analyzing) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="max-w-lg w-full mx-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-surface rounded-xl p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-2 border-[#00F0FF]/30 animate-ping" />
                <div className="absolute inset-0 flex items-center justify-center"><Cpu className="w-8 h-8 text-[#00F0FF] animate-pulse" /></div>
              </div>
              <h2 className="font-['Outfit'] text-2xl text-white mb-2">GPT-5.2 analysiert...</h2>
            </div>
            <div className="space-y-2">
              {analysisSteps.map((s, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-2 rounded transition-all duration-300 ${i <= currentAnalysisStep ? "bg-[#00F0FF]/5 border border-[#00F0FF]/20" : "bg-white/[0.02] border border-white/5"}`}>
                  {i < currentAnalysisStep ? <CheckCircle className="w-4 h-4 text-[#00F0FF]" /> : i === currentAnalysisStep ? <Loader2 className="w-4 h-4 text-[#00F0FF] animate-spin" /> : <div className="w-4 h-4 rounded-full border border-white/10" />}
                  <span className={`text-sm font-['JetBrains_Mono'] ${i <= currentAnalysisStep ? "text-white" : "text-[#606070]"}`}>{s}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="onboarding-wizard" className="min-h-screen pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="font-['Outfit'] text-3xl font-light text-white mb-2">{t.wizard.title}</h1>

        {/* Step indicator */}
        <div className="flex items-center gap-3 mb-8 mt-4">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold font-['JetBrains_Mono'] transition-all ${step > i + 1 ? "bg-[#00F0FF]/20 text-[#00F0FF]" : step === i + 1 ? "bg-[#00F0FF] text-black" : "bg-white/5 text-[#606070]"}`}>
                {step > i + 1 ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              {i < 2 && <div className={`w-12 h-0.5 ${step > i + 1 ? "bg-[#00F0FF]/40" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Category */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h2 className="font-['Outfit'] text-xl text-white mb-1">{t.wizard.step1}</h2>
              <p className="text-sm text-[#A0A0B0] mb-6">{t.wizard.step1_sub}</p>
              <div className="space-y-3">
                {CATEGORIES.map(cat => (
                  <button key={cat.value} data-testid={`cat-${cat.value}`}
                    onClick={() => { setForm(p => ({ ...p, projectCategory: cat.value })); setStep(2); }}
                    className={`w-full flex items-center gap-4 p-5 rounded-xl border transition-all text-left ${form.projectCategory === cat.value ? "border-[#00F0FF]/40 bg-[#00F0FF]/5" : "border-white/10 bg-white/[0.02] hover:bg-white/5"}`}>
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#00F0FF]/10"><cat.icon className="w-6 h-6 text-[#00F0FF]" /></div>
                    <div>
                      <span className="text-white font-medium font-['Outfit'] block">{cat[`label_${lang}`] || cat.label_de}</span>
                      <span className="text-xs text-[#A0A0B0]">{cat[`desc_${lang}`] || cat.desc_de}</span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Audience */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h2 className="font-['Outfit'] text-xl text-white mb-1">{t.wizard.step2}</h2>
              <p className="text-sm text-[#A0A0B0] mb-6">{t.wizard.step2_sub}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#A0A0B0] mb-2 font-['Outfit']">{t.wizard.projectTitle} *</label>
                  <input data-testid="wizard-title" type="text" value={form.projectTitle} onChange={e => setForm(p => ({ ...p, projectTitle: e.target.value }))} placeholder={t.wizard.title_ph}
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm text-[#A0A0B0] mb-2 font-['Outfit']">{t.wizard.audience}</label>
                  <input data-testid="wizard-audience" type="text" value={form.audience} onChange={e => setForm(p => ({ ...p, audience: e.target.value }))} placeholder={t.wizard.users_ph}
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm text-[#A0A0B0] mb-2 font-['Outfit'] flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-[#00F0FF]" />
                    {t.wizard.industry || "Branche"}
                    <span className="text-[10px] tracking-widest uppercase font-['JetBrains_Mono'] text-[#00F0FF]/70">
                      {t.wizard.industry_hint || "für Compliance-Pass"}
                    </span>
                  </label>
                  <select
                    data-testid="wizard-industry"
                    value={form.industry}
                    onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 text-sm transition-colors outline-none rounded-lg appearance-none cursor-pointer"
                    style={{
                      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2300F0FF' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>\")",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 14px center",
                      paddingRight: "36px",
                    }}
                  >
                    <option value="auto" className="bg-[#0A0A14]">{t.wizard.industry_auto || "Automatisch erkennen (empfohlen)"}</option>
                    <option value="banking" className="bg-[#0A0A14]">{t.wizard.ind_banking || "Banking / Finanzdienstleistungen"}</option>
                    <option value="insurance" className="bg-[#0A0A14]">{t.wizard.ind_insurance || "Versicherung"}</option>
                    <option value="healthcare" className="bg-[#0A0A14]">{t.wizard.ind_healthcare || "Healthcare / Medizin"}</option>
                    <option value="pharma" className="bg-[#0A0A14]">{t.wizard.ind_pharma || "Pharma / Life Sciences"}</option>
                    <option value="government" className="bg-[#0A0A14]">{t.wizard.ind_government || "Government / Öffentlicher Sektor / Defense"}</option>
                    <option value="energy" className="bg-[#0A0A14]">{t.wizard.ind_energy || "Energy / Critical Infrastructure (KRITIS)"}</option>
                    <option value="telecom" className="bg-[#0A0A14]">{t.wizard.ind_telecom || "Telecom / Telekommunikation"}</option>
                    <option value="retail" className="bg-[#0A0A14]">{t.wizard.ind_retail || "Retail / E-Commerce"}</option>
                    <option value="manufacturing" className="bg-[#0A0A14]">{t.wizard.ind_manufacturing || "Manufacturing / Industrie"}</option>
                    <option value="logistics" className="bg-[#0A0A14]">{t.wizard.ind_logistics || "Logistics / Transport"}</option>
                    <option value="education" className="bg-[#0A0A14]">{t.wizard.ind_education || "Education / Bildung"}</option>
                    <option value="media" className="bg-[#0A0A14]">{t.wizard.ind_media || "Media / Entertainment"}</option>
                    <option value="real_estate" className="bg-[#0A0A14]">{t.wizard.ind_real_estate || "Real Estate / Immobilien"}</option>
                    <option value="consulting" className="bg-[#0A0A14]">{t.wizard.ind_consulting || "Consulting / Services"}</option>
                    <option value="other" className="bg-[#0A0A14]">{t.wizard.ind_other || "Andere / Nicht reguliert"}</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-8">
                <button onClick={() => setStep(1)} className="btn-secondary flex items-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> {t.wizard.back}</button>
                <button onClick={() => { if (form.projectTitle.trim()) setStep(3); else setError(lang === "de" ? "Bitte gib einen Titel ein." : "Please enter a title."); }} className="btn-primary flex items-center gap-2 text-sm">{t.wizard.next} <ArrowRight className="w-4 h-4" /></button>
              </div>
              {error && <p className="text-[#FF3B30] text-sm mt-3">{error}</p>}
            </motion.div>
          )}

          {/* Step 3: Description */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h2 className="font-['Outfit'] text-xl text-white mb-1">{t.wizard.step3}</h2>
              <p className="text-sm text-[#A0A0B0] mb-6">{t.wizard.step3_sub}</p>
              <div>
                <label className="block text-sm text-[#A0A0B0] mb-2 font-['Outfit']">{t.wizard.description} *</label>
                <textarea data-testid="wizard-description" value={form.projectDescription} onChange={e => setForm(p => ({ ...p, projectDescription: e.target.value }))} rows={8} placeholder={t.wizard.desc_ph}
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none resize-none rounded-lg" />
              </div>
              <div className="flex items-center gap-3 mt-8">
                <button onClick={() => setStep(2)} className="btn-secondary flex items-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> {t.wizard.back}</button>
                <button data-testid="wizard-analyze-btn" onClick={handleSubmit} className="btn-primary flex items-center gap-2 text-sm"><Cpu className="w-4 h-4" /> {t.wizard.analyze} <ArrowRight className="w-4 h-4" /></button>
              </div>
              {error && <p className="text-[#FF3B30] text-sm mt-3">{error}</p>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
